import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../services/firebase";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  FiMenu,
  FiX,
  FiTrash2,
  FiEdit2,
  FiCheck,
  FiFileText,
  FiUpload,
  FiClock,
  FiHome,
  FiSearch,
  FiFilter,
  FiChevronRight,
  FiDownload,
  FiExternalLink,
  FiLoader
} from "react-icons/fi";
import Lottie from "lottie-react";
import SelectDocument from "../assets/lotties/Select-Document.json";
import { useLocation, Link, useNavigate } from "react-router-dom";
import AskAI from "../components/AskAI";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/fr";

dayjs.locale("fr");
dayjs.extend(relativeTime);

const ConfirmModal = ({ isOpen, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6 animate-fadeIn">
      <div className="bg-white p-6 md:p-8 rounded-2xl shadow-2xl max-w-md w-full relative transform transition-all duration-300 scale-95 animate-modalEnter">
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
          aria-label="Fermer"
        >
          <FiX size={20} />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-red-100 to-red-50 rounded-full flex items-center justify-center shadow-inner">
            <FiTrash2 className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Confirmer la suppression</h3>
          <p className="text-gray-600">{message}</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="flex-1 px-5 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium hover:shadow-sm active:scale-95"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-5 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl active:scale-95"
          >
            Supprimer définitivement
          </button>
        </div>
      </div>
    </div>
  );
};

const HistoryPanel = () => {
  const { user, loading: authLoading } = useAuth(); // Récupérez authLoading
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const sidebarRef = useRef(null);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Détecter les tailles d'écran
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Fermer le sidebar quand on clique en dehors (mobile)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        isMobile
      ) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sidebarOpen, isMobile]);

  // Charger les documents
  useEffect(() => {
    if (!user) {
      setDocumentsLoading(false);
      return;
    }

    const fetchDocs = async () => {
      setDocumentsLoading(true);
      try {
        const q = query(
          collection(db, "documents"),
          where("userId", "==", user.uid),
          orderBy("uploadedAt", "desc")
        );
        const snap = await getDocs(q);
        const docs = snap.docs.map((doc) => ({ 
          id: doc.id, 
          ...doc.data(),
          uploadedAt: doc.data().uploadedAt?.toDate?.() || new Date()
        }));
        setDocuments(docs);
        setFilteredDocs(docs);

        // Sélectionner le document approprié
        if (location.state?.newDocId) {
          const newDoc = docs.find((d) => d.id === location.state.newDocId);
          if (newDoc) {
            setSelectedDoc(newDoc);
            if (isMobile) setSidebarOpen(false);
          }
        } else if (docs.length > 0 && !selectedDoc) {
          setSelectedDoc(docs[0]);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des documents:", error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocs();
  }, [user]);

  // Filtrer les documents
  useEffect(() => {
    let result = documents;

    // Recherche par texte
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(doc =>
        doc.fileName.toLowerCase().includes(term) ||
        doc.summary?.toLowerCase().includes(term)
      );
    }

    // Filtre par type
    if (filterType !== "all") {
      const now = new Date();
      switch (filterType) {
        case "today":
          result = result.filter(doc => {
            const docDate = new Date(doc.uploadedAt);
            return docDate.toDateString() === now.toDateString();
          });
          break;
        case "week":
          const weekAgo = new Date(now.setDate(now.getDate() - 7));
          result = result.filter(doc => new Date(doc.uploadedAt) > weekAgo);
          break;
        case "month":
          const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
          result = result.filter(doc => new Date(doc.uploadedAt) > monthAgo);
          break;
      }
    }

    setFilteredDocs(result);
  }, [searchTerm, filterType, documents]);

  const handleDeleteClick = (docItem, e) => {
    e?.stopPropagation();
    setDocToDelete(docItem);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!docToDelete) return;
    try {
      await deleteDoc(doc(db, "documents", docToDelete.id));
      const updatedDocs = documents.filter((d) => d.id !== docToDelete.id);
      setDocuments(updatedDocs);
      setFilteredDocs(updatedDocs);
      
      if (selectedDoc?.id === docToDelete.id) {
        const nextDoc = updatedDocs[0] || null;
        setSelectedDoc(nextDoc);
      }
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
    } finally {
      setConfirmOpen(false);
      setDocToDelete(null);
    }
  };

  const handleEditClick = (docItem, e) => {
    e.stopPropagation();
    setEditId(docItem.id);
    setEditName(docItem.fileName);
  };

  const handleRenameConfirm = async () => {
    if (!editName.trim()) return;
    try {
      await updateDoc(doc(db, "documents", editId), { fileName: editName });
      const updatedDocs = documents.map((d) =>
        d.id === editId ? { ...d, fileName: editName } : d
      );
      setDocuments(updatedDocs);
      setFilteredDocs(updatedDocs);
      if (selectedDoc?.id === editId) {
        setSelectedDoc({ ...selectedDoc, fileName: editName });
      }
    } catch (error) {
      console.error("Erreur lors du renommage:", error);
    } finally {
      setEditId(null);
      setEditName("");
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    
    const now = dayjs();
    const docDate = dayjs(date);
    const diffDays = now.diff(docDate, 'day');

    if (diffDays === 0) {
      return "Aujourd'hui, " + docDate.format("HH:mm");
    } else if (diffDays === 1) {
      return "Hier, " + docDate.format("HH:mm");
    } else if (diffDays < 7) {
      return docDate.format("dddd, HH:mm");
    } else {
      return docDate.format("DD MMM YYYY");
    }
  };

  const getFileIconColor = (index) => {
    const colors = [
      "bg-blue-100 text-blue-600",
      "bg-teal-100 text-teal-600",
      "bg-purple-100 text-purple-600",
      "bg-amber-100 text-amber-600",
      "bg-pink-100 text-pink-600",
      "bg-indigo-100 text-indigo-600",
    ];
    return colors[index % colors.length];
  };

  // Écran de chargement pendant la vérification d'authentification
  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6">
            <div className="w-full h-full border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          {/* <h2 className="text-xl font-bold text-gray-800 mb-3">Chargement...</h2>
          <p className="text-gray-600">Vérification de votre session</p> */}
        </div>
      </div>
    );
  }

  // Si pas d'utilisateur (seulement après que le chargement d'authentification est terminé)
  if (!user && !authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md text-center px-4">
          <div className="w-24 h-24 mx-auto mb-6 bg-teal-100 rounded-full flex items-center justify-center">
            <FiHome className="w-12 h-12 text-teal-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Connectez-vous
          </h2>
          <p className="text-gray-600 mb-8">
            Accédez à vos documents et conversations.
          </p>
          <div className="flex flex-col gap-3">
            <Link
              to="/"
              className="px-6 py-3 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors"
            >
              Retour à l'accueil
            </Link>
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-white text-teal-600 border border-teal-500 rounded-lg font-medium hover:bg-teal-50 transition-colors"
            >
              Se connecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Écran de chargement pendant le chargement des documents
  if (documentsLoading) {
    return (
      <div className={`${isMobile ? "min-h-screen" : "flex flex-col h-screen"} bg-gray-50`}>
        <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm px-4 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center">
                <FiFileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Mes Documents</h1>
                <p className="text-gray-600">Chargement...</p>
              </div>
            </div>
          </div>
        </header>
        
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6">
              <div className="w-full h-full border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-600">Chargement de vos documents...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isMobile ? "min-h-screen" : "flex flex-col h-screen"} bg-gray-50`}>
      {/* Header amélioré */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            {/* Bouton menu pour mobile/tablet */}
            <button
              className={`p-3 rounded-xl hover:bg-gray-100 transition-colors ${
                isTablet ? "md:hidden" : "md:hidden"
              }`}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Ouvrir le menu des documents"
            >
              <FiMenu size={24} className="text-gray-700" />
            </button>

            {/* Titre et statistiques */}
            <div className="flex items-center gap-4 ml-4">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                <FiFileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Mes Documents</h1>
                <div className="flex items-center gap-4 mt-1">
                  <span className="text-sm text-gray-600">
                    {documents.length} document{documents.length !== 1 ? "s" : ""}
                  </span>
                  <span className="text-sm text-teal-600 font-medium">
                    {filteredDocs.length} visible{filteredDocs.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="hidden sm:flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium text-gray-700 hover:shadow-sm"
              >
                <FiHome size={18} />
                <span>Accueil</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside
          ref={sidebarRef}
          className={`
            bg-white border-r border-gray-200 flex flex-col
            transition-all duration-300 ease-in-out
            ${isMobile ? "fixed inset-y-0 left-0 w-80 z-50" : "relative"}
            ${isTablet ? "w-64" : "w-80"}
            ${sidebarOpen || !isMobile ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          {/* Header sidebar */}
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Historique</h2>
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              )}
            </div>

            {/* Barre de recherche */}
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un document..."
                className="w-full pl-10 pr-4 py-3 bg-gray-100 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <FiX className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>

            {/* Filtres */}
            <div className="mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors w-full"
              >
                <FiFilter size={16} />
                <span>Filtrer</span>
                <FiChevronRight
                  className={`ml-auto transform transition-transform ${
                    showFilters ? "rotate-90" : ""
                  }`}
                  size={16}
                />
              </button>

              {showFilters && (
                <div className="mt-2 grid grid-cols-2 gap-2 animate-slideDown">
                  {[
                    { id: "all", label: "Tous" },
                    { id: "today", label: "Aujourd'hui" },
                    { id: "week", label: "Cette semaine" },
                    { id: "month", label: "Ce mois" },
                  ].map((filter) => (
                    <button
                      key={filter.id}
                      onClick={() => setFilterType(filter.id)}
                      className={`px-3 py-2 text-sm rounded-lg transition-all ${
                        filterType === filter.id
                          ? "bg-teal-500 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Liste des documents */}
          <div className="flex-1 overflow-y-auto p-4">
            {filteredDocs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6 shadow-inner">
                  <FiUpload className="w-16 h-16 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {searchTerm ? "Aucun résultat" : "Aucun document"}
                </h3>
                <p className="text-gray-600 max-w-xs mb-6">
                  {searchTerm
                    ? "Aucun document ne correspond à votre recherche"
                    : "Commencez par uploader votre premier document"}
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                  >
                    Effacer la recherche
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredDocs.map((docItem, index) => {
                  const isSelected = selectedDoc?.id === docItem.id;
                  const isEditing = editId === docItem.id;

                  return (
                    <div
                      key={docItem.id}
                      onClick={() => {
                        if (!isEditing) {
                          setSelectedDoc(docItem);
                          if (isMobile) setSidebarOpen(false);
                        }
                      }}
                      className={`
                        group relative p-4 rounded-xl transition-all duration-300 cursor-pointer
                        ${
                          isSelected
                            ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg transform scale-[1.02]"
                            : "bg-white hover:bg-gray-50 border border-gray-300 hover:border-teal-300 hover:shadow-md"
                        }
                      `}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icône */}
                        <div
                          className={`
                            w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                            ${
                              isSelected
                                ? "bg-white/20"
                                : getFileIconColor(index)
                            }
                          `}
                        >
                          <FiFileText
                            className={`w-6 h-6 ${
                              isSelected ? "text-white" : ""
                            }`}
                          />
                        </div>

                        {/* Contenu */}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="space-y-3">
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleRenameConfirm();
                                  if (e.key === "Escape") {
                                    setEditId(null);
                                    setEditName("");
                                  }
                                }}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                                autoFocus
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRenameConfirm();
                                  }}
                                  className="px-3 py-1.5 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 transition-colors flex items-center gap-2"
                                >
                                  <FiCheck size={14} />
                                  Sauvegarder
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditId(null);
                                    setEditName("");
                                  }}
                                  className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-400 transition-colors"
                                >
                                  Annuler
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex justify-between items-start">
                                <p className="font-semibold truncate text-base">
                                  {docItem.fileName}
                                </p>
                                {isSelected && (
                                  <FiChevronRight className="ml-2 flex-shrink-0" />
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <span
                                  className={`text-sm ${
                                    isSelected
                                      ? "text-teal-100"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {formatDate(docItem.uploadedAt)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Actions */}
                        {!isEditing && !isMobile && (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => handleEditClick(docItem, e)}
                              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                              title="Renommer"
                            >
                              <FiEdit2
                                className={`w-4 h-4 ${
                                  isSelected
                                    ? "text-white"
                                    : "text-gray-600 hover:text-teal-600"
                                }`}
                              />
                            </button>
                            <button
                              onClick={(e) => handleDeleteClick(docItem, e)}
                              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                              title="Supprimer"
                            >
                              <FiTrash2
                                className={`w-4 h-4 ${
                                  isSelected
                                    ? "text-white"
                                    : "text-gray-600 hover:text-red-600"
                                }`}
                              />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Actions mobiles */}
                      {!isEditing && isMobile && (
                        <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-gray-300/30">
                          <button
                            onClick={(e) => handleEditClick(docItem, e)}
                            className="px-3 py-1.5 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                          >
                            Renommer
                          </button>
                          <button
                            onClick={(e) => handleDeleteClick(docItem, e)}
                            className="px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                          >
                            Supprimer
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer sidebar */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">
                  {filteredDocs.length} document{filteredDocs.length !== 1 ? "s" : ""}
                </p>
                <p className="text-xs text-gray-500">
                  {documents.length - filteredDocs.length} filtré{documents.length - filteredDocs.length !== 1 ? "s" : ""}
                </p>
              </div>
              <Link
                to="/"
                className="px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all duration-300 shadow hover:shadow-md"
              >
                <FiUpload className="inline mr-2" />
                Nouveau
              </Link>
            </div>
          </div>
        </aside>

        {/* Overlay pour mobile */}
        {isMobile && sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Contenu principal */}
        <main className="flex-1 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
          {selectedDoc ? (
            <AskAI document={selectedDoc} />
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-6">
              <div className="max-w-md text-center">
                <div className="w-64 h-64 mb-8">
                  <Lottie
                    animationData={SelectDocument}
                    loop={true}
                    className="drop-shadow-2xl"
                  />
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Sélectionnez un document
                </h2>
                <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                  Choisissez un document dans votre historique pour commencer une conversation avec l'assistant IA
                </p>
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95"
                >
                  Voir mes documents
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Modal de confirmation */}
      <ConfirmModal
        isOpen={confirmOpen}
        message={`Êtes-vous sûr de vouloir supprimer "${docToDelete?.fileName}" ? Cette action est irréversible.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setDocToDelete(null);
        }}
      />

      {/* Bouton flottant pour ouvrir le sidebar sur mobile */}
      {isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed bottom-6 left-6 z-40 w-14 h-14 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full shadow-2xl flex items-center justify-center hover:from-teal-600 hover:to-teal-700 transition-all duration-300 hover:scale-110 active:scale-95"
          aria-label="Ouvrir l'historique"
        >
          <FiFileText size={24} />
        </button>
      )}
    </div>
  );
};

export default HistoryPanel;